import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: `了解${siteInfo.name}的品牌故事、发展历程与联系方式`,
  url: `${siteInfo.url}/about`,
  inLanguage: 'zh-CN',
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    foundingDate: siteInfo.founded,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: siteInfo.faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 国产久久久"
        description={`了解${siteInfo.name}的品牌故事、核心优势与联系方式。${siteInfo.description.substring(0, 80)}`}
        keywords="国产久久久,关于我们,品牌介绍,联系方式,内容平台"
        canonicalUrl="/about"
        ogType="website"
        schema={[aboutSchema, faqSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-4">关于我们</h1>
          </div>
        </div>

        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-medium text-primary-600 mb-3">品牌介绍</p>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                  关于{siteInfo.name}
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  {siteInfo.name}成立于{siteInfo.founded}年，是国内领先的精选内容长效聚合平台。
                  我们致力于汇聚海量优质国产内容，通过长效稳定的更新机制和智能推荐算法，
                  为每一位用户提供持久流畅的优质内容体验。
                </p>
                <p className="text-base text-foreground-600 leading-relaxed mb-4">
                  平台内容涵盖电影、电视剧、纪录片、综艺、动画等全品类，每日更新超过200部优质作品。
                  无论您是寻找经典回顾还是追逐最新热播，{siteInfo.name}都能满足您的长期需求。
                </p>
                <p className="text-base text-foreground-600 leading-relaxed">
                  自上线以来，{siteInfo.name}已累计服务超过{siteInfo.stats.activeUsers}用户，
                  用户满意度高达{siteInfo.stats.satisfaction}。稳定、可靠、值得收藏——这是我们不变的承诺。
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden h-[400px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20modern%20Chinese%20office%20space%20with%20minimalist%20design%2C%20creative%20team%20collaboration%20atmosphere%2C%20soft%20natural%20lighting%20through%20large%20windows%2C%20warm%20wooden%20furniture%2C%20premium%20corporate%20photography%2C%20clean%20aesthetic&width=700&height=500&seq=about-brand-image&orientation=landscape"
                    alt={`${siteInfo.name} - 品牌形象`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary-600 mb-3">核心优势</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">为什么选择我们</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {siteInfo.features.map((feature, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-accent-600 mb-3">用户口碑</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">用户真实评价</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {siteInfo.testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary-600 mb-3">常见问题</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">您可能想了解</h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="space-y-4">
                {siteInfo.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                      <span>{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary-600 mb-3">联系我们</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">随时与我们取得联系</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center p-8 rounded-xl bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">电子邮箱</h3>
                <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                  {siteInfo.email}
                </a>
              </div>
              <div className="text-center p-8 rounded-xl bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-phone-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">客服电话</h3>
                <span className="text-sm text-foreground-500">{siteInfo.phone}</span>
              </div>
              <div className="text-center p-8 rounded-xl bg-background-100">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-map-pin-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">公司地址</h3>
                <span className="text-sm text-foreground-500">{siteInfo.address}</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}